import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';

// The ui-shared Button is a div, so these forms have no submit button for the browser to submit with.
export function submitOnEnter(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.requestSubmit();
}

export function submitClosestForm(event: MouseEvent<HTMLElement>) {
    event.currentTarget.closest('form')?.requestSubmit();
}

/**
 * Per field feedback on top of a cross-state form: validation errors show once the user has edited the field or a submit
 * failed validation (`hasTriggeredValidations`), and an error from the server replaces them until the field is edited again.
 */
export function useFieldFeedback<TField extends string>() {
    const [touched, setTouched] = useState<ReadonlySet<TField>>(new Set());
    const [serverErrors, setServerErrors] = useState<Partial<Record<TField, ReactNode>>>({});

    return {
        error: (name: TField, validationError: ReactNode, hasTriggeredValidations = false) =>
            serverErrors[name] ?? (touched.has(name) || hasTriggeredValidations ? validationError : undefined),
        setError: (name: TField, error: ReactNode) => setServerErrors((errors) => ({ ...errors, [name]: error })),
        changed: (name: TField) => {
            setTouched((touched) => (touched.has(name) ? touched : new Set(touched).add(name)));
            setServerErrors((errors) => ({ ...errors, [name]: undefined }));
        },
        reset: () => {
            setTouched(new Set());
            setServerErrors({});
        },
    };
}
